import { HeadingSection, Modal, ModalBody, ModalHeader, Text } from 'bold-ui'
import { Form, FormRenderProps } from 'components/form'
import { confirm, ConfirmConfigCustomRender } from 'components/modals/confirm'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  Cid10NotificacaoAgravoModel,
  NotificacaoAgravoFormModel,
  NotificacaoAgravoStep,
} from './model-notificacaoAgravo'
import { NotificacaoAgravoModalFooter } from './NotificacaoAgravoModalFooter'
import { NotificacaoAgravoModalSidebar } from './NotificacaoAgravoModalSidebar'
import { DadosClinicosStep } from './steps/DadosClinicosStep'
import { DadosIdentificacaoStep } from './steps/DadosIdentificacaoStep'
import { DadosIndividuoStep } from './steps/DadosIndividuoStep'
import { NotificacaoStep } from './steps/NotificacaoStep'
import {
  dadosClinicosStepValidator,
  dadosIdentificacaoStepValidator,
  dadosIndividuoStepValidator,
  notificacaoStepValidator,
} from './validator-notificacaoAgravo'

// TODO: remover ao concluir o formulário (#35068).
const IS_VALIDACAO_PASSOS_DESABILITADA: boolean = true

interface NotificacaoAgravoModalProps {
  cid10s: ReadonlyArray<Cid10NotificacaoAgravoModel>
  initialValues?: NotificacaoAgravoFormModel
  onClose: () => void
  onSubmit: (values: NotificacaoAgravoFormModel) => void
}

export function NotificacaoAgravoModal(props: Readonly<NotificacaoAgravoModalProps>) {
  const { cid10s, initialValues, onClose, onSubmit } = props

  const [activeStepIdx, setActiveStepIdx] = useState(0)
  const [completedStepsIdx, setCompletedStepsIdx] = useState<number[]>([])
  const modalBodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (modalBodyRef.current) modalBodyRef.current.scrollTop = 0
  }, [activeStepIdx])

  const steps = useMemo<NotificacaoAgravoStep[]>(
    () => [
      {
        title: 'Dados de identificação',
        render: () => <DadosIdentificacaoStep />,
        validator: dadosIdentificacaoStepValidator,
      },
      {
        title: 'Dados do indivíduo',
        render: (formProps) => <DadosIndividuoStep formProps={formProps} />,
        validator: dadosIndividuoStepValidator,
      },
      {
        title: 'Dados clínicos',
        render: () => <DadosClinicosStep cid10s={cid10s} />,
        validator: dadosClinicosStepValidator,
      },
      {
        title: 'Notificação',
        render: () => <NotificacaoStep />,
        validator: notificacaoStepValidator,
      },
    ],
    [cid10s]
  )

  const activeStep = steps[activeStepIdx]
  const isFirstStep = activeStepIdx === 0
  const isLastStep = activeStepIdx === steps.length - 1

  const handleClose = (isDirty: boolean) =>
    isDirty ? confirm({ ...closeModalConfigs, onConfirm: onClose })() : onClose()

  const handleGoBack = () => {
    setCompletedStepsIdx((prev) => prev.filter((idx) => idx < activeStepIdx - 1))
    setActiveStepIdx((prev) => prev - 1)
  }

  const handleFormSubmit = (values: NotificacaoAgravoFormModel) => {
    if (isLastStep) {
      onSubmit(values)
    } else {
      setCompletedStepsIdx((prev) => [...prev, activeStepIdx])
      setActiveStepIdx((prev) => prev + 1)
    }
  }

  const renderModal = (renderProps: FormRenderProps<NotificacaoAgravoFormModel>) => (
    <Modal open size='large' closeOnBackdropClick={false} onClose={() => handleClose(renderProps.dirty)}>
      <ModalHeader
        title='Notificação de agravo'
        subtitle='Os dados preenchidos neste formulário serão enviados para o SINAN'
      />
      <NotificacaoAgravoModalSidebar
        activeStepIdx={activeStepIdx}
        completedStepsIdx={completedStepsIdx}
        steps={steps}
      />
      <ModalBody ref={modalBodyRef} data-testid='notificacao-agravo-modal-body'>
        <HeadingSection level={2} title={activeStep.title}>
          {activeStep.render(renderProps)}
        </HeadingSection>
      </ModalBody>
      <NotificacaoAgravoModalFooter
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        onClose={() => handleClose(renderProps.dirty)}
        onGoBack={handleGoBack}
        onSubmit={renderProps.handleSubmit}
      />
    </Modal>
  )

  return (
    <Form<NotificacaoAgravoFormModel>
      initialValues={initialValues ?? EMPTY_INITIAL_VALUES}
      validate={IS_VALIDACAO_PASSOS_DESABILITADA ? undefined : activeStep.validator}
      onSubmit={handleFormSubmit}
      render={renderModal}
    />
  )
}

const EMPTY_INITIAL_VALUES: NotificacaoAgravoFormModel = {}

const closeModalConfigs: ConfirmConfigCustomRender = {
  title: 'Deseja cancelar a notificação de agravo?',
  icon: {
    name: 'questionMarkOutline',
    fill: 'secondary',
  },
  confirmLabel: 'Sim, cancelar',
  cancelLabel: 'Não, continuar aqui',
  type: 'primary',
  size: 'auto',
  depthLevel: 2,
  render: () => <Text>As alterações realizadas serão perdidas</Text>,
}
